import {
	PublicKey,
	SystemProgram,
	SYSVAR_RENT_PUBKEY,
	Transaction,
	TransactionInstruction,
} from "@solana/web3.js"

import {
	ASSOCIATED_TOKEN_PROGRAM_ID,
	TOKEN_PROGRAM_ID,
	Token,
} from "@solana/spl-token"
import { Metadata } from "@metaplex-foundation/mpl-token-metadata"
import * as bs58 from "bs58"

/* global BigInt */

const process = process.env.NODE_ENV

let REWARD_MINT_DECIMALS = -1
async function getRewardMintDecimals(connection, rewardmint) {
	if (REWARD_MINT_DECIMALS === -1) {
		let rewardMintInfo = await connection.getParsedAccountInfo(
			new PublicKey(rewardmint)
		)
		let data = rewardMintInfo?.value?.data
		if (data && !(data instanceof Buffer))
			REWARD_MINT_DECIMALS = data.parsed.info.decimals
		else REWARD_MINT_DECIMALS = 9
	}
	return REWARD_MINT_DECIMALS
}

function createInstructionData(instruction) {
	if (instruction === "Stake") return Buffer.from([1])
	else if (instruction === "Unstake") return Buffer.from([2])
	else if (instruction === "StakeWithdraw") return Buffer.from([5])

	throw new Error(`Unrecognized instruction: ${instruction}`)
}

function parseUint64Le(data, offset = 0) {
	let number = BigInt(0)
	for (let i = 0; i < 8; i++)
		number += BigInt(data[offset + i]) << BigInt(i * 8)
	return number
}

export function getAssociatedTokenAddress(
	walletAddress,
	tokenAddress,
	allowOffCurve = true
) {
	return Token.getAssociatedTokenAddress(
		ASSOCIATED_TOKEN_PROGRAM_ID,
		TOKEN_PROGRAM_ID,
		tokenAddress,
		walletAddress,
		allowOffCurve
	)
}

function transactionKey(pubkey, isSigner, isWritable = true) {
	return {
		pubkey,
		isSigner,
		isWritable,
	}
}

const VAULT_PREFIX = "vault"
export async function getVaultAddress(pid) {
	let [address] = await PublicKey.findProgramAddress(
		[Buffer.from(VAULT_PREFIX)],
		new PublicKey(pid)
	)
	return address
}

export async function getStakeDataAddress(token, pid) {
	let [address] = await PublicKey.findProgramAddress(
		[token.toBytes()],
		new PublicKey(pid)
	)
	return address
}

export async function checkCandyMachineStakable(
	PROGRAM_ID,
	connection,
	candyMachine,
	forceRefresh = false
) {
	return (
		(await getCandyMachineRate(
			PROGRAM_ID,
			connection,
			candyMachine,
			forceRefresh
		)) != null
	)
}

const CANDY_MACHINE_RATE_CACHE = new Map()
export async function getCandyMachineRate(
	PROGRAM_ID,
	connection,
	candyMachine,
	forceRefresh = false
) {
	if (forceRefresh || !CANDY_MACHINE_RATE_CACHE.has(candyMachine)) {
		let whitelistDataAddress = await getWhitelistDataAddress(
			new PublicKey(candyMachine),
			PROGRAM_ID
		)
		//console.log(whitelistDataAddress.toString(), "WHITELISDT")
		let price = null
		let whitelistData = await connection.getAccountInfo(whitelistDataAddress)

		if (whitelistData != null) price = parseUint64Le(whitelistData.data)

		CANDY_MACHINE_RATE_CACHE.set(candyMachine, price)
	}

	return CANDY_MACHINE_RATE_CACHE.get(candyMachine)
}

const WHITELIST_PREFIX = "whitelist"
export async function getWhitelistDataAddress(candyMachine, PROGRAM_ID) {
	let [address] = await PublicKey.findProgramAddress(
		[Buffer.from(WHITELIST_PREFIX), candyMachine.toBytes()],
		new PublicKey(PROGRAM_ID)
	)
	return address
}

export async function getRewardCalculator(connection, PROGRAM_ID) {
	let vaultAddress = await getVaultAddress(PROGRAM_ID)
	let valutAccountInfo = await connection.getAccountInfo(vaultAddress)
	if (!valutAccountInfo) throw new Error(`${vaultAddress} not initialized`)
	let { data } = valutAccountInfo
	///console.log(valutAccountInfo, "\vault")
	return {
		minPeriod: parseUint64Le(data, 0),
		rewardPeriod: parseUint64Le(data, 8),
	}
}

export function calculateRewards(stakedData, now) {
	if (!stakedData.REWARDS) return

	let nowTimestamp = BigInt(Math.floor(now.getTime() / 1000))
	let { timestamp, withdrawn, rewardMultiplier, decimals, REWARDS } = stakedData
	let { minPeriod, rewardPeriod, bonus, bonus_period } = stakedData.REWARDS

	let period = nowTimestamp - timestamp
	//if (period < minPeriod) return "0"

	const REWARD = Number(
		(BigInt(rewardMultiplier) * period) / BigInt(rewardPeriod) -
			BigInt(withdrawn)
	)

	// console.log({
	// 	REWARD: REWARD,
	// 	BONUS: BONUS,
	// 	REWARDS: { ...stakedData.REWARDS },
	// })
	const r = {
		NFT: stakedData.mint,
		REWARD_MINT: stakedData.REWARDS.REWARD_MINT.pubkey,
		amount: REWARD,
	}
	return r
}
// const BONUS = (BigInt(bonus) * period) / BigInt(bonus_period)
// const REWARD =
// 	period < minPeriod
// 		? 0
// 		: ((rewardMultiplier * period) / rewardPeriod - withdrawn) /
// 		  BigInt(10 ** decimals)
// console.log({
// 	REWARD: REWARD,
// 	BONUS: BONUS,
// 	REWARDS: { ...stakedData.REWARDS },
// })
// return {
// 	NFT: stakedData.mint,
// 	REWARD_MINT: stakedData.REWARDS.REWARD_MINT.pubkey,
// 	amount: REWARD,
// 	bonus: BONUS,
export async function getStakedDataByOwner(
	connection,
	owner,
	program_id,
	rewards
) {
	let stakeDataAccounts = await connection.getProgramAccounts(
		new PublicKey(program_id),
		{
			filters: [
				createStakeTokenOwnerFilter(owner),
				createStakeTokenActiveFilter(),
			],
		}
	)

	//	getRewardCalculator
	return Promise.all(
		stakeDataAccounts.map(async ({ account: { data } }) => {
			let timestamp = parseUint64Le(data, 0)
			let mint = new PublicKey(data.slice(40, 72))
			let metadata = await Metadata.load(
				connection,
				await Metadata.getPDA(mint)
			)
			let json = await fetch(metadata.data.data.uri).then((e) => e.json())
			let withdrawn = parseUint64Le(data, 73)

			let candyMachineAddress = metadata.data.data.creators[0].address
			let rewardMultiplier = await getCandyMachineRate(
				program_id,
				connection,
				candyMachineAddress
			)
			if (rewardMultiplier == null) {
				console.trace(
					`Warning: null rewardMultiplier for candy machine: ${candyMachineAddress}`
				)
				rewardMultiplier = BigInt(1)
			}
			let decimals = await getRewardMintDecimals(
				connection,
				rewards.REWARD_MINT.pubkey
			)
			let rewardCalculator = await getRewardCalculator(connection, program_id)
			let stakehold = (await parseInt(timestamp + rewards.minPeriod, 10)) * 1000

			return {
				mint,
				data: metadata.data.data,
				json,
				timestamp,
				withdrawn,
				rewardMultiplier,
				decimals,
				pid: program_id,
				REWARDS: rewards,
				stakehold,
			}
		})
	)
}

export function createStakeTokenOwnerFilter(owner) {
	return {
		memcmp: {
			offset: 8,
			bytes: owner.toBase58(),
		},
	}
}

export function createStakeTokenActiveFilter(active = true) {
	return {
		memcmp: {
			offset: 72,
			bytes: bs58.encode([active ? 1 : 0]),
		},
	}
}

export async function createStakeTokenTransaction(
	connection,
	owner,
	token,
	pid
) {
	let metadata = await Metadata.load(connection, await Metadata.getPDA(token))
	let candyMachineAddress = metadata.data.data.creators[0].address
	let candyMachine = new PublicKey(candyMachineAddress)

	let sourceTokenAccount = null
	let { value: accounts } = await connection.getTokenLargestAccounts(token)
	for (let { address, amount } of accounts)
		if (amount === "1") sourceTokenAccount = address
	if (!sourceTokenAccount)
		throw new Error(`Could not get current owner for ${token}`)

	let transaction = new Transaction()
	transaction.add(
		await createStakeTokenInstruction(
			owner,
			token,
			candyMachine,
			sourceTokenAccount,
			pid
		)
	)

	let primarySourceTokenAccount = await getAssociatedTokenAddress(owner, token)
	if (!primarySourceTokenAccount.equals(sourceTokenAccount))
		transaction.add(
			Token.createCloseAccountInstruction(
				TOKEN_PROGRAM_ID,
				sourceTokenAccount,
				primarySourceTokenAccount,
				owner,
				[]
			)
		)

	return transaction
}
export async function createStakeTokenInstruction(
	owner,
	token,
	candyMachine,
	sourceTokenAccount,
	pid
) {
	let vaultAddress = await getVaultAddress(pid)
	let metadataAddress = await Metadata.getPDA(token)

	if (!sourceTokenAccount)
		sourceTokenAccount = await getAssociatedTokenAddress(owner, token)
	let destinationTokenAccount = await getAssociatedTokenAddress(
		vaultAddress,
		token,
		true
	)

	let stakeDataAddress = await getStakeDataAddress(token, pid)
	let whitelistDataAddress = await getWhitelistDataAddress(candyMachine, pid)

	return new TransactionInstruction({
		programId: pid,
		data: createInstructionData("Stake"),
		keys: [
			transactionKey(owner, true),
			transactionKey(token, false, false),
			transactionKey(metadataAddress, false, false),

			transactionKey(vaultAddress, false, false),
			transactionKey(sourceTokenAccount, false),
			transactionKey(destinationTokenAccount, false),

			transactionKey(TOKEN_PROGRAM_ID, false, false),
			transactionKey(SystemProgram.programId, false, false),
			transactionKey(SYSVAR_RENT_PUBKEY, false, false),
			transactionKey(ASSOCIATED_TOKEN_PROGRAM_ID, false, false),

			transactionKey(stakeDataAddress, false),
			transactionKey(whitelistDataAddress, false),
		],
	})
}

export async function createUnstakeTokenTransaction(connection, owner, nft) {
	let metadata = await Metadata.load(
		connection,
		await Metadata.getPDA(nft.mint)
	)
	let candyMachineAddress = metadata.data.data.creators[0].address
	let candyMachine = new PublicKey(candyMachineAddress)

	let transaction = new Transaction()
	transaction.add(await createUnstakeTokenInstruction(owner, candyMachine, nft))
	return transaction
}
export async function createUnstakeTokenInstruction(owner, candyMachine, nft) {
	let vaultAddress = nft.REWARDS.vault.address
	let metadataAddress = await Metadata.getPDA(nft.mint)

	let sourceTokenAccount = await getAssociatedTokenAddress(
		vaultAddress,
		nft.mint,
		true
	)
	///console.log(sourceTokenAccount, "ADSFASDFASDFASDFSADFSADFd")
	let destinationTokenAccount = await getAssociatedTokenAddress(owner, nft.mint)

	let sourceRewardAccount = await getAssociatedTokenAddress(
		vaultAddress,
		new PublicKey(nft.REWARDS.REWARD_MINT.pubkey),
		true
	)
	let destinationRewardAccount = await getAssociatedTokenAddress(
		owner,
		new PublicKey(nft.REWARDS.REWARD_MINT.pubkey)
	)

	let stakeDataAddress = await getStakeDataAddress(nft.mint, nft.pid)
	let whitelistDataAddress = await getWhitelistDataAddress(
		candyMachine,
		nft.pid
	)

	return new TransactionInstruction({
		programId: nft.pid,
		data: createInstructionData("Unstake"),
		keys: [
			transactionKey(owner, true),
			transactionKey(SystemProgram.programId, false, false),
			transactionKey(nft.mint, false, false),

			transactionKey(TOKEN_PROGRAM_ID, false, false),
			transactionKey(SYSVAR_RENT_PUBKEY, false, false),
			transactionKey(ASSOCIATED_TOKEN_PROGRAM_ID, false, false),

			transactionKey(stakeDataAddress, false),
			transactionKey(vaultAddress, false, false),

			transactionKey(destinationRewardAccount, false),
			transactionKey(sourceRewardAccount, false),
			transactionKey(destinationTokenAccount, false),
			transactionKey(sourceTokenAccount, false),

			transactionKey(metadataAddress, false, false),
			transactionKey(whitelistDataAddress, false),

			transactionKey(
				new PublicKey(nft.REWARDS.REWARD_MINT.pubkey),
				false,
				false
			),
		],
	})
}

export async function createWithdrawRewardTransaction(
	connection,
	owner,
	token,
	pid,
	REWARD_MINT
) {
	let metadata = await Metadata.load(connection, await Metadata.getPDA(token))
	let candyMachineAddress = metadata.data.data.creators[0].address
	let candyMachine = new PublicKey(candyMachineAddress)

	let transaction = new Transaction()
	await transaction.add(
		await createWithdrawRewardInstruction(
			owner,
			token,
			candyMachine,
			pid,
			REWARD_MINT
		)
	)
	return transaction
}
export async function createWithdrawRewardInstruction(
	owner,
	token,
	candyMachine,
	pid,
	REWARD_MINT
) {
	//console.log(REWARD_MINT)
	let vaultAddress = await getVaultAddress(pid)
	let metadataAddress = await Metadata.getPDA(token)

	let sourceTokenAccount = await getAssociatedTokenAddress(
		vaultAddress,
		token,
		true
	)
	let destinationTokenAccount = await getAssociatedTokenAddress(owner, token)

	let sourceRewardAccount = await getAssociatedTokenAddress(
		vaultAddress,
		new PublicKey(REWARD_MINT.pubkey),
		true
	)
	let destinationRewardAccount = await getAssociatedTokenAddress(
		owner,
		new PublicKey(REWARD_MINT.pubkey)
	)

	let stakeDataAddress = await getStakeDataAddress(token, pid)
	let whitelistDataAddress = await getWhitelistDataAddress(candyMachine, pid)

	return new TransactionInstruction({
		programId: pid,
		data: createInstructionData("StakeWithdraw"),
		keys: [
			transactionKey(owner, true),
			transactionKey(SystemProgram.programId, false, false),
			transactionKey(token, false, false),

			transactionKey(TOKEN_PROGRAM_ID, false, false),
			transactionKey(SYSVAR_RENT_PUBKEY, false, false),
			transactionKey(ASSOCIATED_TOKEN_PROGRAM_ID, false, false),

			transactionKey(stakeDataAddress, false),
			transactionKey(vaultAddress, false, false),

			transactionKey(destinationRewardAccount, false),
			transactionKey(sourceRewardAccount, false),
			transactionKey(destinationTokenAccount, false),
			transactionKey(sourceTokenAccount, false),

			transactionKey(metadataAddress, false, false),
			transactionKey(whitelistDataAddress, false),

			transactionKey(new PublicKey(REWARD_MINT.pubkey), false, false),
		],
	})
}
